import type { U3206DataAccessRow, U3206DataDomainRef } from '../U3206Service';

export const U3206_DATA_ACCESS_PROFILES = ['minimal', 'project_manager', 'full', 'custom'] as const;

export type U3206DataAccessProfile = (typeof U3206_DATA_ACCESS_PROFILES)[number];

export type U3206PresetDataAccessProfile = Exclude<U3206DataAccessProfile, 'custom'>;

/** Catalogue domaines V1 — contrôle par entité (fallback si initLU vide). */
export const U3206_ALL_DATA_DOMAIN_CODES = [
  'project.operational',
  'project.billing_financial',
  'timesheet.operational',
  'wip.operational',
  'wip.billing',
  'invoice.operational',
  'invoice.billing',
  'employee.hourly_rate',
  'employee.personal_contact',
] as const;

/** Domaines opérationnels (sans montants ni paie) — profil minimal. */
export const U3206_MINIMAL_DATA_DOMAIN_CODES = [
  'project.operational',
  'timesheet.operational',
  'wip.operational',
  'invoice.operational',
] as const;

const U3206_PROFILE_ALLOWED_DOMAINS: Record<U3206PresetDataAccessProfile, readonly string[]> = {
  minimal: [...U3206_MINIMAL_DATA_DOMAIN_CODES],
  project_manager: [
    ...U3206_MINIMAL_DATA_DOMAIN_CODES,
    'project.billing_financial',
    'wip.billing',
    'invoice.billing',
  ],
  full: [...U3206_ALL_DATA_DOMAIN_CODES],
};

export type U3206DataAccessRowInput = Pick<U3206DataAccessRow, 'dataDomainCode' | 'allowed'>;

/**
 * Génère les lignes depuis un preset.
 * Si `domains` (catalogue initLU) est fourni, s'appuie sur `defaultProfileCodes` ;
 * sinon fallback sur la carte V1 hardcodée (9 domaines).
 */
export function u3206BuildDataAccessRowsFromProfile(
  profile: U3206PresetDataAccessProfile,
  domains?: U3206DataDomainRef[],
): U3206DataAccessRowInput[] {
  if (domains && domains.length > 0) {
    return [...domains]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((domain) => ({
        dataDomainCode: domain.code,
        allowed: domain.defaultProfileCodes.includes(profile),
      }));
  }

  const allowedSet = new Set(U3206_PROFILE_ALLOWED_DOMAINS[profile]);
  return U3206_ALL_DATA_DOMAIN_CODES.map((code) => ({
    dataDomainCode: code,
    allowed: allowedSet.has(code),
  }));
}

export function u3206MatchesPresetProfile(
  profile: U3206PresetDataAccessProfile,
  rows: U3206DataAccessRowInput[],
  domains?: U3206DataDomainRef[],
): boolean {
  const expected = u3206BuildDataAccessRowsFromProfile(profile, domains);
  if (rows.length !== expected.length) {
    return false;
  }
  const byCode = new Map(rows.map((row) => [row.dataDomainCode, row.allowed]));
  return expected.every((row) => byCode.get(row.dataDomainCode) === row.allowed);
}

export function u3206GroupDomainsByTheme(domains: U3206DataDomainRef[]): Map<string, U3206DataDomainRef[]> {
  const grouped = new Map<string, U3206DataDomainRef[]>();
  const sorted = [...domains].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const domain of sorted) {
    const list = grouped.get(domain.themeCode) ?? [];
    list.push(domain);
    grouped.set(domain.themeCode, list);
  }
  return grouped;
}

export function u3206ResolveDomainLabel(domain: U3206DataDomainRef, locale: string): string {
  return locale.startsWith('fr') ? domain.labelFr : domain.labelEn;
}

export function u3206ResolveDomainDescription(domain: U3206DataDomainRef, locale: string): string {
  const fr = domain.descriptionFr?.trim() ?? '';
  const en = domain.descriptionEn?.trim() ?? '';
  return locale.startsWith('fr') ? fr || en : en || fr;
}

export function u3206ComputeDataAccessPreview(
  domains: U3206DataDomainRef[],
  rows: U3206DataAccessRowInput[],
): { allowedQuestions: string[]; blockedQuestions: string[] } {
  const allowedByCode = new Map(rows.map((row) => [row.dataDomainCode, row.allowed]));
  const allowedQuestions: string[] = [];
  const blockedQuestions: string[] = [];
  const seenAllowed = new Set<string>();
  const seenBlocked = new Set<string>();

  for (const domain of domains) {
    const isAllowed = allowedByCode.get(domain.code) ?? false;
    if (isAllowed) {
      for (const question of domain.exampleAllowedQuestions) {
        if (seenAllowed.has(question)) continue;
        seenAllowed.add(question);
        allowedQuestions.push(question);
      }
    } else {
      for (const question of domain.exampleBlockedQuestions) {
        if (seenBlocked.has(question)) continue;
        seenBlocked.add(question);
        blockedQuestions.push(question);
      }
    }
  }

  return { allowedQuestions, blockedQuestions };
}

export function u3206IsDataAccessCustomized(
  profile: U3206DataAccessProfile,
  rows: U3206DataAccessRowInput[],
  domains?: U3206DataDomainRef[],
): boolean {
  if (profile === 'custom') {
    return true;
  }
  return !u3206MatchesPresetProfile(profile, rows, domains);
}

/**
 * Garantit une ligne par domaine du catalogue (conserve id/version existants).
 */
export function u3206EnsureDataAccessRows(
  rows: U3206DataAccessRow[],
  domains: U3206DataDomainRef[],
): U3206DataAccessRow[] {
  if (domains.length === 0) {
    return rows.length > 0 ? rows : u3206BuildDataAccessRowsFromProfile('minimal');
  }

  const byCode = new Map(rows.map((row) => [row.dataDomainCode, row]));
  return [...domains]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((domain) => {
      const existing = byCode.get(domain.code);
      if (existing) {
        return existing;
      }
      return {
        dataDomainCode: domain.code,
        allowed: domain.defaultProfileCodes.includes('minimal'),
      };
    });
}
